import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("fp_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("fp_user", JSON.stringify(r.data));
    } catch (e) {
      if (!e?.response) {
        // Network failure (offline / server unreachable) — keep the cached
        // session so offline features (queued workout logging) still work.
        console.warn("[auth] offline — using cached session");
        try {
          const cached = JSON.parse(localStorage.getItem("fp_user") || "null");
          setUser(cached);
        } catch {
          setUser(null);
        }
      } else {
        // Token is present but /auth/me failed — most likely expired or revoked.
        // Log it so we can spot cascading auth failures in the browser console.
        console.error("[auth] session refresh failed:", e?.response?.status, e?.message);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth, skip the /me check —
    // GoogleAuthCallback will exchange the session_id and establish the
    // session first (prevents a 401 race).
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("fp_token", r.data.access_token);
    localStorage.setItem("fp_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  const signup = async (payload) => {
    const r = await api.post("/auth/signup", payload);
    localStorage.setItem("fp_token", r.data.access_token);
    localStorage.setItem("fp_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  const setSessionFromToken = async (access_token, userObj) => {
    localStorage.setItem("fp_token", access_token);
    if (userObj) localStorage.setItem("fp_user", JSON.stringify(userObj));
    setUser(userObj || null);
  };

  const logout = () => {
    // Best-effort: clear the Google session cookie server-side too.
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("fp_token");
    localStorage.removeItem("fp_user");
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh, setSessionFromToken }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
